<template>
  <div class="profile-image-uploader">
    <label class="profile-image-uploader__dropzone" for="profile-image-input">
      <input
          id="profile-image-input"
          type="file"
          accept="image/jpeg,image/png,image/gif"
          :disabled="uploading"
          class="profile-image-uploader__input"
          @change="previewImage"
      >
      <span class="profile-image-uploader__preview" :class="{ 'has-image': preview }">
        <img v-if="preview" :src="preview" alt="Profile preview">
        <span v-else class="profile-image-uploader__placeholder">+</span>
      </span>
      <span class="profile-image-uploader__content">
        <strong>{{ preview ? 'Change profile image' : 'Upload profile image' }}</strong>
        <small>Click to choose a JPG, PNG, or GIF (under 5MB).</small>
        <small v-if="statusMessage" class="profile-image-uploader__status" :class="statusClass" role="status">
          {{ statusMessage }}
        </small>
      </span>
    </label>
  </div>
</template>

<script>

import {updateProfileImage} from "@/api/image";

export default {
  name: 'ImageUploader',
  props: {
    initialImageUrl: {
      type: String,
      default: '',
    },
  },
  data: () => {
    return {
      preview: null,
      image: null,
      previewObjectUrl: null,
      uploading: false,
      uploadGeneration: 0,
      statusMessage: '',
      statusType: ''
    };
  },
  computed: {
    statusClass() {
      return {
        'is-success': this.statusType === 'success',
        'is-error': this.statusType === 'error',
      };
    },
  },
  watch: {
    initialImageUrl: {
      immediate: true,
      handler(value) {
        if (!this.image) {
          this.preview = value || null;
        }
      },
    },
  },
  beforeUnmount() {
    this.uploadGeneration += 1;
    this.releasePreview();
  },
  methods: {
    releasePreview() {
      if (this.previewObjectUrl) URL.revokeObjectURL(this.previewObjectUrl);
      this.previewObjectUrl = null;
    },
    async previewImage(event) {
      const input = event.target;
      const file = input.files?.[0];
      input.value = '';
      if (!file || this.uploading) return;
      if (!['image/jpeg', 'image/png', 'image/gif'].includes(file.type)) {
        this.statusMessage = 'Please choose a JPG, PNG, or GIF image.';
        this.statusType = 'error';
        return;
      }
      // Match the Storage rule's strict upper bound.
      if (file.size >= 5 * 1024 * 1024) {
        this.statusMessage = 'Please choose an image smaller than 5MB.';
        this.statusType = 'error';
        return;
      }
      const generation = ++this.uploadGeneration;
      this.releasePreview();
      this.image = file;
      this.previewObjectUrl = URL.createObjectURL(file);
      this.preview = this.previewObjectUrl;
      this.uploading = true;
      this.statusMessage = 'Uploading...';
      this.statusType = 'neutral';
      try {
        await updateProfileImage(file);
        if (generation !== this.uploadGeneration) return;
        this.statusMessage = 'Profile image updated.';
        this.statusType = 'success';
      } catch (error) {
        if (generation !== this.uploadGeneration) return;
        console.error(error);
        this.releasePreview();
        this.image = null;
        this.preview = this.initialImageUrl || null;
        this.statusMessage = 'Upload failed. Please choose the image again to retry.';
        this.statusType = 'error';
      } finally {
        if (generation === this.uploadGeneration) this.uploading = false;
      }
    },
    reset() {
      this.uploadGeneration += 1;
      this.releasePreview();
      this.image = null;
      this.preview = this.initialImageUrl || null;
      this.uploading = false;
      this.statusMessage = '';
      this.statusType = '';
    },
  },
};
</script>

<style lang="scss" scoped>
@use '../utils/_tokens.scss' as *;

.profile-image-uploader {
  max-width: 420px;
}

.profile-image-uploader__dropzone {
  display: flex;
  align-items: center;
  gap: 1rem;
  width: 100%;
  padding: 1rem;
  border: 1px dashed #C9CBD8;
  border-radius: 14px;
  background: #F4F4F8;
  cursor: pointer;
  transition: border-color .15s ease, background .15s ease, box-shadow .15s ease;

  &:hover {
    border-color: $blue-1;
    background: #FBF0C9;
    box-shadow: 0 0 0 3px rgba(27, 79, 191, .08);
  }
}

.profile-image-uploader__input {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

.profile-image-uploader__preview {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 96px;
  height: 96px;
  border-radius: 50%;
  background: #E2E3EC;
  color: $blue-1;
  font-size: 2.25rem;
  font-weight: 300;
  overflow: hidden;

  &.has-image {
    background: #fff;
    border: 2px solid #fff;
    box-shadow: 0 8px 20px rgba(15, 23, 42, .12);
  }

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
}

.profile-image-uploader__content {
  display: flex;
  flex-direction: column;
  gap: .25rem;
  color: #23253A;

  strong {
    font-size: .95rem;
    font-weight: 700;
  }

  small {
    color: #5A5D75;
    font-size: .82rem;
  }
}

.profile-image-uploader__status {
  margin-top: .2rem;

  &.is-success {
    color: #15803D;
  }

  &.is-error {
    color: #B91C1C;
  }
}
</style>
